import type { ReactNode } from "react";

export interface HeaderProps {
  /**
   * Left cluster — typically a brand wordmark + logo. Maps to v35's
   * `.app-header-left` (logo + `.app-title`). Clickable home affordance
   * is the consumer's concern (wrap the node in a button/link).
   */
  brand?: ReactNode;
  /**
   * Right cluster — typically the theme toggle + profile menu. Maps to
   * v35's `.app-header-right`.
   */
  actions?: ReactNode;
  /** Optional extra classes for the outer bar. */
  className?: string;
}

/**
 * Header — surface-agnostic top bar.
 *
 * v35 `.app-header`: 56px tall, surface bg, 1px bottom border, 28px
 * horizontal padding, brand cluster on the left and an actions cluster
 * on the right (`justify-between`). This primitive owns the bar chrome
 * + spacing only; brand/actions content is composed by the surface
 * (web composes its own, extension uses its own popup chrome).
 *
 * Tokenized: no arbitrary values — `bg-surface` / `border-border` /
 * Tailwind's default 4px spacing scale (`px-7` = 28px, `h-14` = 56px).
 *
 * `data-token-scope="brand-header"` is what makes the bar emerald in the
 * light theme: `tokens.css` re-declares the base tokens for this subtree
 * under `[data-theme="light"]`, so the classes here and in the slotted
 * content stay the same in both themes and dark is untouched. Custom
 * properties inherit through the DOM: anything rendered as a DOM child of
 * this element takes the scope, a portalled menu does not. Read the note on
 * that rule before adding a child that uses a token the scope does not list.
 */
export function Header({ brand, actions, className }: HeaderProps) {
  const bar =
    "flex items-center justify-between gap-4 h-14 px-7 bg-surface border-b border-border shrink-0";

  return (
    <header
      data-testid="app-header"
      data-token-scope="brand-header"
      className={[bar, className].filter(Boolean).join(" ")}
    >
      <div className="flex items-center gap-3 min-w-0">{brand}</div>
      <div className="flex items-center gap-3.5">{actions}</div>
    </header>
  );
}
